import React, { useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { Error, Loading, AbsoluteCenter } from 'components';
import { useGlobalContext } from 'constants/context';
import useBreakpoint from 'hooks/useBreakpoint';
import Home from './Home';
import { useInfiniteCatalogScroll } from './hooks/useInfiniteCatalogScroll';
import { useIsInitialLoading } from './hooks/useIsInitialLoading';
import { useFiltersFetch } from './hooks/useFiltersFetch';
import { sortOptions } from './SortSelect';
import catalogCache from './homeStateCache';

const defaultSort = sortOptions.find(({ value }) => value === 'latest_added');

export default function HomeContainer() {
  // The scroll offset to land on, captured before the scroll listener below can
  // overwrite it: 0 on a fresh visit, the saved offset on browser back.
  const targetScrollRef = useRef(catalogCache.scrollY);
  const scrollRestoredRef = useRef(false);

  const { contextState: { searchValue, filters: selectedFilters = [], sort = defaultSort }, setContextState } = useGlobalContext();
  // Sort lives in the global context rather than in local state so that opening
  // an item and coming back keeps the sorting the user picked.
  const setSort = useCallback((newSort) => setContextState((contextState) => ({ ...contextState, sort: newSort })), [setContextState]);
  const setSelectedFilters = useCallback((newValue) => setContextState((contextState) => ({ ...contextState, filters: (typeof newValue === 'function') ? newValue(contextState.filters || []) : newValue, searchValue: '' })), [setContextState]);
  const { /* loading: loadingFilters, */ filters } = useFiltersFetch();
  const { breakpoint } = useBreakpoint();
  const { infiniteRef, error, items, refetch, loading, count } = useInfiniteCatalogScroll({ searchValue, sort: sort.value, filters: selectedFilters });
  const isInitialLoading = useIsInitialLoading(loading, items.length > 0);

  useEffect(() => {
    const onScroll = () => { catalogCache.scrollY = window.scrollY; };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Restore once, as soon as the (cached) items are in the DOM — the image
  // containers reserve their height up front, so the offset stays accurate even
  // before the images themselves have loaded.
  const hasItems = items.length > 0;
  useLayoutEffect(() => {
    if (scrollRestoredRef.current || !hasItems) return;
    scrollRestoredRef.current = true;
    window.scrollTo(0, targetScrollRef.current);
  }, [hasItems]);

  if (isInitialLoading) return (<AbsoluteCenter><Loading center /></AbsoluteCenter>);
  if (error) return <Error error={error} retry={refetch} />;

  return (
    <Home
      ref={infiniteRef}
      loading={loading}
      items={items}
      sort={sort}
      setSort={setSort}
      filters={filters}
      searchValue={searchValue}
      selectedFilters={selectedFilters}
      setSelectedFilters={setSelectedFilters}
      count={count}
      breakpoint={breakpoint}
    />
  );
}
